import React from "react";
import {useTranslation} from "@unibuddy/intl";

import {yearsValueToTranslationKey} from "../../../../../shared/Mentor/components/MentorCard/MentorCard";
import {MentorFieldUI} from "./MentorFieldUI";
import {Degrees} from "./Degrees";
import {Country} from "./Country";
import {MentorFieldCustomLabel, PublicMentorField} from "../../../../../types/generated";
import {IntlNamespaces} from "../../../../../translations/types";
import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import {
    INTL_ABOUT_ME,
    INTL_AREA_OF_STUDY_SECTION,
    INTL_CURRENTLY_IN,
    INTL_FAVORITE_COURSES,
    INTL_HOBBIES_INTERESTS,
    INTL_I_COME_FROM,
    INTL_I_SPEAK,
    INTL_NOT_PART_OF_SOCIETIES,
    INTL_PREVIOUS_STUDIES,
    INTL_SOCIETIES,
} from "../../../../../translations/mentorCard";

const itemSeparator = " · ";

type Translation = (key: string, defaultLabel: string) => string;

type RenderProps = {
    mentor: PublicMentorField;
    t?: Translation;
    mentorFieldLabels?: MentorFieldCustomLabel[];
    isDegreeTypesEnabled: boolean;
};

type MentorComponentProps = {
    field: string;
    mentor: PublicMentorField;
};

const fieldConfig = {
    country: {
        render: ({mentor, mentorFieldLabels}: RenderProps) => {
            if (!mentor.country) return null;
            const customLabel =
                mentorFieldLabels.find((label) => label.key === "country")?.customLabel ?? null;
            return (
                <MentorFieldUI
                    translationKey={customLabel ? "" : INTL_I_COME_FROM.key}
                    defaultLabel={customLabel ?? (INTL_I_COME_FROM.defaultValue as string)}
                    fieldValue={
                        <Country
                            mentorFields={mentor?.mentorFields}
                            placeName={mentor?.placeName}
                        />
                    }
                />
            );
        },
    },
    year: {
        render: ({mentor, t}: RenderProps) => {
            const {degreeLevel, year} = mentor;

            if (!year) return null;

            const key = yearsValueToTranslationKey[year];
            const undergraduateKey = `undergraduateYears.${key}`;
            const postgraduateKey = `postgraduateYears.${key}`;
            const label =
                degreeLevel === "Undergraduate"
                    ? t(undergraduateKey, year)
                    : t(postgraduateKey, year);

            return (
                <MentorFieldUI
                    translationKey={INTL_CURRENTLY_IN.key}
                    defaultLabel={INTL_CURRENTLY_IN.defaultValue as string}
                    fieldValue={label}
                />
            );
        },
    },
    degrees: {
        render: ({mentor, isDegreeTypesEnabled}: RenderProps) =>
            mentor?.degrees?.length > 0 && isDegreeTypesEnabled ? (
                <Degrees degrees={mentor.degrees} />
            ) : null,
    },
    languages: {
        render: ({mentor, t}: RenderProps) =>
            mentor?.languages?.length > 0 ? (
                <MentorFieldUI
                    translationKey={INTL_I_SPEAK.key}
                    defaultLabel={INTL_I_SPEAK.defaultValue as string}
                    fieldValue={mentor.languages
                        .map((language) => t(`languages:${language?.name}`, language?.name))
                        .join(itemSeparator)}
                />
            ) : null,
    },
    degree: {
        render: ({mentor, mentorFieldLabels, isDegreeTypesEnabled}: RenderProps) => {
            if (!mentor?.degree?.name || isDegreeTypesEnabled) return null;

            const customLabel =
                mentorFieldLabels.find((label) => label.key === "degree")?.customLabel ?? null;

            return (
                <MentorFieldUI
                    translationKey={customLabel ? "" : INTL_AREA_OF_STUDY_SECTION.key}
                    defaultLabel={
                        customLabel ?? (INTL_AREA_OF_STUDY_SECTION.defaultValue as string)
                    }
                    fieldValue={mentor.degree.name}
                />
            );
        },
    },
    favouriteModules: {
        render: ({mentor}: RenderProps) =>
            mentor?.favouriteModules?.length > 0 ? (
                <MentorFieldUI
                    translationKey={INTL_FAVORITE_COURSES.key}
                    defaultLabel={INTL_FAVORITE_COURSES.defaultValue as string}
                    fieldValue={mentor.favouriteModules
                        .map((module) => module.name)
                        .join(itemSeparator)}
                />
            ) : null,
    },
    pastQualification: {
        render: ({mentor}: RenderProps) =>
            mentor?.pastQualification ? (
                <MentorFieldUI
                    translationKey={INTL_PREVIOUS_STUDIES.key}
                    defaultLabel={INTL_PREVIOUS_STUDIES.defaultValue as string}
                    fieldValue={mentor.pastQualification}
                />
            ) : null,
    },
    interests: {
        render: ({mentor}: RenderProps) =>
            mentor?.interests?.length > 0 ? (
                <MentorFieldUI
                    translationKey={INTL_HOBBIES_INTERESTS.key}
                    defaultLabel={INTL_HOBBIES_INTERESTS.defaultValue as string}
                    fieldValue={mentor.interests
                        .map((interest) => interest?.name)
                        .join(itemSeparator)}
                />
            ) : null,
    },
    societies: {
        render: ({mentor, t}: RenderProps) => (
            <MentorFieldUI
                translationKey={INTL_SOCIETIES.key}
                defaultLabel={INTL_SOCIETIES.defaultValue as string}
                fieldValue={
                    mentor?.societies?.length > 0
                        ? mentor.societies.map((society) => society?.name).join(itemSeparator)
                        : t(
                              INTL_NOT_PART_OF_SOCIETIES.key,
                              INTL_NOT_PART_OF_SOCIETIES.defaultValue as string,
                          )
                }
            />
        ),
    },
    bio: {
        render: ({mentor}: RenderProps) => {
            let bio = null;
            if (mentor.bio && mentor.bio.length > 0) {
                bio = mentor.bio.length > 200 ? `${mentor.bio.substring(0, 200)}...` : mentor.bio;
            } else if (mentor.shortBio) {
                bio = `${mentor.shortBio}...`;
            }
            return bio ? (
                <MentorFieldUI
                    translationKey={INTL_ABOUT_ME.key}
                    defaultLabel={INTL_ABOUT_ME.defaultValue as string}
                    fieldValue={bio}
                />
            ) : null;
        },
    },
};

export function MentorFields({field, mentor}: MentorComponentProps) {
    const {t} = useTranslation(IntlNamespaces.MENTOR_CARD);
    const {mentorFieldLabels, features} = useUniversity();
    const isDegreeTypesEnabled = features.get("enableDegreeTypes") ?? false;

    const fieldComponent = fieldConfig[field];

    if (fieldComponent) {
        return fieldComponent.render({
            mentor,
            t,
            mentorFieldLabels,
            isDegreeTypesEnabled,
        }) as React.JSX.Element | null;
    }

    const customField = mentor?.customAttributes?.find(
        (customAttribute) => customAttribute?.attribute?.id === field,
    );

    if (customField?.option?.value) {
        return (
            <MentorFieldUI
                key={customField?.attribute?.name ?? ""}
                translationKey={customField?.attribute?.name ?? ""}
                defaultLabel={customField?.attribute?.name ?? ""}
                fieldValue={customField?.option?.value}
            />
        );
    }
    return null;
}
